import { useState, useEffect } from 'react';
import { 
    Box, Grid, Card, CardContent, Typography, Avatar, 
    LinearProgress, Skeleton, Stack, Chip, useTheme
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMoneyBillTrendUp, faTicket, faEye, faPercent, faGift
} from '@fortawesome/free-solid-svg-icons';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';


import { getEventDashboardStats } from '../../../../controllers/company/events/MerchantEventController';

export default function EventDashboard({ eventId }) {
    const theme = useTheme();
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (eventId) {
            fetchDashboardData();
        }
    }, [eventId]);

    const fetchDashboardData = async () => {
        setIsLoading(true);
        try {
            let response = await getEventDashboardStats(eventId);
            if(response.success) setStats(response.data);
            

        } catch (error) {
            console.error("Error cargando dashboard:", error);
            setIsLoading(false);
        }
        setIsLoading(false)
    };

    // Función auxiliar para colorear la barra de progreso
    const getProgressColor = (percent) => {
        if (percent >= 80) return 'success';
        if (percent >= 50) return 'warning';
        return 'primary';
    };

    // Estilo unificado para las tarjetas
    const cardStyle = { 
        borderRadius: 4, 
        border: '1px solid #e0e0e0', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
        height: '100%'
    };

    if (isLoading || !stats) {
        return (
            <Grid container spacing={3}>
                {[1, 2, 3, 4].map(i => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 4 }} />
                    </Grid>
                ))}
                <Grid item xs={12} md={8}><Skeleton variant="rounded" height={350} sx={{ borderRadius: 4 }} /></Grid>
                <Grid item xs={12} md={4}><Skeleton variant="rounded" height={350} sx={{ borderRadius: 4 }} /></Grid>
            </Grid>
        );
    }

    const { kpis, grafico_tendencias, progreso_secciones } = stats;

    return (
        <Box>
            {/* --- BLOQUE 1: KPIs --- */}
            <Grid container spacing={3} mb={4}>
                
                {/* KPI: Ingresos */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle} elevation={0}>
                        <CardContent>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 48, height: 48, borderRadius: 3 }}>
                                    <FontAwesomeIcon icon={faMoneyBillTrendUp} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                                        Ingresos Brutos
                                    </Typography>
                                    <Typography variant="h5" fontWeight="800" color="#111">
                                        ${kpis.ingresos_brutos.toLocaleString('en-US', {minimumFractionDigits: 2})}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* KPI: Ventas Totales */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle} elevation={0}>
                        <CardContent>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 48, height: 48, borderRadius: 3 }}>
                                    <FontAwesomeIcon icon={faTicket} />
                                </Avatar>
                                <Box width="100%">
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                                        Boletos Vendidos
                                    </Typography>
                                    <Box display="flex" alignItems="baseline" gap={1}>
                                        <Typography variant="h5" fontWeight="800" color="#111">
                                            {kpis.boletos_vendidos}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                                            / {kpis.capacidad_total}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* KPI: Conversión */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle} elevation={0}>
                        <CardContent>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: '#fff3e0', color: '#ed6c02', width: 48, height: 48, borderRadius: 3 }}>
                                    <FontAwesomeIcon icon={faPercent} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                                        Tasa de Conversión
                                    </Typography>
                                    <Typography variant="h5" fontWeight="800" color="#111">
                                        {kpis.tasa_conversion}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* KPI: Vistas y Cortesías (Combinado) */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle} elevation={0}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <FontAwesomeIcon icon={faEye} color={theme.palette.text.secondary} />
                                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                                        Vistas Únicas:
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontWeight="bold">{kpis.vistas_totales}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <FontAwesomeIcon icon={faGift} color={theme.palette.text.secondary} />
                                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                                        Cortesías:
                                    </Typography>
                                </Box>
                                <Chip label={kpis.boletos_cortesia} size="small" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>

            {/* --- BLOQUE 2: GRÁFICOS Y BARRAS --- */}
            <Grid container spacing={3}>
                
                {/* Gráfico de Tendencias */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ ...cardStyle, p: 2 }} elevation={0}>
                        <Typography variant="h6" fontWeight="bold" mb={3} px={1}>
                            Rendimiento de los últimos 7 días
                        </Typography>
                        <Box sx={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <LineChart data={grafico_tendencias} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                    <XAxis 
                                        dataKey="fecha" 
                                        tickFormatter={(tick) => new Date(tick).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                                        tick={{ fontSize: 12, fill: '#888' }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    {/* Eje Y Izquierdo para Vistas (Números grandes) */}
                                    <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                                    {/* Eje Y Derecho para Ventas (Números pequeños) */}
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                                    
                                    <Tooltip 
                                        contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        labelFormatter={(label) => new Date(label).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                                    />
                                    <Legend iconType="circle" wrapperStyle={{ paddingTop: 20 }} />
                                    
                                    <Line yAxisId="left" type="monotone" dataKey="vistas" name="Vistas" stroke="#ed6c02" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    <Line yAxisId="right" type="monotone" dataKey="ventas" name="Boletos Vendidos" stroke="#1976d2" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Card>
                </Grid>

                {/* Progreso por Secciones */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ ...cardStyle, p: 2 }} elevation={0}>
                        <Typography variant="h6" fontWeight="bold" mb={3} px={1}>
                            Ocupación por Zonas
                        </Typography>
                        
                        <Stack spacing={3} px={1}>
                            {progreso_secciones.map((sec, index) => (
                                <Box key={index}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={1}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="#333">
                                            {sec.nombre}
                                        </Typography>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            {sec.vendidos} / {sec.capacidad} ({sec.porcentaje}%)
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={sec.porcentaje} 
                                        color={getProgressColor(sec.porcentaje)}
                                        sx={{ height: 8, borderRadius: 4, bgcolor: '#f0f0f0' }}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </Card>
                </Grid>

            </Grid>
        </Box>
    );
}